// DevItemsScreen - ② 개발품 상세관리의 진입 목록 (결정 S-2).
//
// 개발품을 특정하지 않고 상세화면을 열 수 없으므로, NAV 로 직접 들어온
// 사용자에게 먼저 선택 목록을 보여준다. Dashboard 와 **같은 표 컴포넌트**를
// 쓴다 - 12열 배치를 두 번 구현하면 열 위치가 어긋나고, 그건 §20 "10초
// 파악" 을 깨뜨린다.
import { DevItemTable } from "@/components/DevItemTable";
import {
  Button,
  Checkbox,
  DataBoundary,
  EmptyState,
  FilterBar,
  Icon,
  Select,
  TableSkeleton,
  TextInput,
} from "@/components/ui";
import type { Customer, DashboardSort, Staff } from "@/lib/api";
import { asOfLabel } from "@/lib/dates";
import { useDashboard } from "@/lib/hooks";
import type { ScreenNav } from "@/screens/nav";
import { useState } from "react";

export const DevItemsScreen = ({
  nav,
  customers,
  staff,
}: {
  nav: ScreenNav;
  customers: readonly Customer[];
  staff: readonly Staff[];
}) => {
  const [q, setQ] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [staffId, setStaffId] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [sort, setSort] = useState<DashboardSort>("default");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  const { summary, items } = useDashboard({ q, customerId, staffId, includeArchived }, sort, order);
  const filtered = !!q || !!customerId || !!staffId || includeArchived;

  const onSort = (col: DashboardSort) => {
    if (sort === col) {
      setOrder(order === "asc" ? "desc" : "asc");
      return;
    }
    setSort(col);
    setOrder(col === "progress" || col === "status" ? "desc" : "asc");
  };

  const reset = () => {
    setQ("");
    setCustomerId("");
    setStaffId("");
    setIncludeArchived(false);
    setSort("default");
    setOrder("asc");
  };

  return (
    <div className="flex flex-col gap-s5">
      <header className="flex flex-wrap items-baseline justify-between gap-s3">
        <div className="flex flex-col gap-s1">
          <p className="text-label" style={{ color: "var(--text-secondary)" }}>
            품명을 선택하면 상세관리 화면으로 들어갑니다.
          </p>
          <p
            className="text-caption tabular-nums"
            data-num=""
            style={{ color: "var(--text-tertiary)" }}
          >
            {asOfLabel(summary.data?.asOf ?? "")}
          </p>
        </div>
        <Button
          variant="primary"
          size="md"
          icon={<Icon name="plus" size={15} />}
          onClick={nav.openCreate}
        >
          개발품 등록
        </Button>
      </header>

      <div className="overflow-hidden rounded-card" style={{ background: "var(--surface)" }}>
        <FilterBar
          right={
            <Button variant="ghost" size="sm" onClick={reset} disabled={!filtered}>
              초기화
            </Button>
          }
        >
          <span className="w-[16rem]">
            <TextInput
              type="search"
              value={q}
              onChange={setQ}
              ariaLabel="품명 · 품번 · 프로젝트 검색"
              placeholder="품명 · 품번 · 프로젝트 검색"
            />
          </span>
          <span className="w-[9rem]">
            <Select
              value={customerId}
              onChange={setCustomerId}
              options={customers
                .filter((c) => c.active)
                .map((c) => ({ value: c.id, label: c.name }))}
              ariaLabel="고객사로 조회"
              placeholder="고객사 전체"
            />
          </span>
          <span className="w-[9rem]">
            <Select
              value={staffId}
              onChange={setStaffId}
              options={staff.filter((s) => s.active).map((s) => ({ value: s.id, label: s.name }))}
              ariaLabel="담당자로 조회"
              placeholder="담당자 전체"
            />
          </span>
          <Checkbox checked={includeArchived} onChange={setIncludeArchived} label="종료 포함" />
        </FilterBar>

        <DataBoundary
          loading={items.loading}
          error={items.error}
          onRetry={() => void items.reload()}
          skeleton={<TableSkeleton rows={10} cols={13} />}
        >
          {items.data.items.length === 0 ? (
            filtered ? (
              <EmptyState
                icon="search"
                title="조건에 맞는 개발품이 없어요"
                description="조건에 맞는 개발품이 없어요. 필터를 조정해 보세요."
                action={
                  <Button variant="secondary" size="md" onClick={reset}>
                    필터 초기화
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon="list"
                title="등록된 개발품이 없어요"
                description="아직 등록된 개발품이 없어요. 첫 개발품을 등록하면 여기에 진행 현황이 모여요."
                action={
                  <Button variant="primary" size="md" onClick={nav.openCreate}>
                    개발품 등록
                  </Button>
                }
              />
            )
          ) : (
            <DevItemTable
              items={items.data.items}
              sort={sort}
              order={order}
              onSort={onSort}
              onOpenItem={nav.openItem}
            />
          )}
        </DataBoundary>
      </div>
    </div>
  );
};
